/** @jsxImportSource @emotion/react */
import { EmptyContent, Header } from '@quickbit/backoffice-core-frontend';
import {
  Card,
  createStyles,
  DateInput,
  Loader,
  Table,
  Typography,
  UtilityButton,
  UtilitySelect,
  useTheme,
} from '@quickbit/qb-design-system';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CaseStatisticsResponse,
  GroupCount,
  useGetCaseStatistics,
} from 'api/useCaseStatisticsApi';

type Metric = 'created' | 'managed' | 'reportedToFipo';
type Granularity = 'month' | 'year';
type GroupBy = 'rule' | 'title';

const METRIC_OPTIONS: { value: Metric; label: string }[] = [
  { value: 'created', label: 'Created' },
  { value: 'managed', label: 'Managed (closed)' },
  { value: 'reportedToFipo', label: 'Reported to FIPO' },
];

const GRANULARITY_OPTIONS: { value: Granularity; label: string }[] = [
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly' },
];

const GROUP_BY_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: 'rule', label: 'By rule / Chainalysis' },
  { value: 'title', label: 'By case title' },
];

const CUSTOMER_TYPE_OPTIONS = [
  { value: '', label: 'All customers' },
  { value: 'APP', label: 'App' },
  { value: 'MERCHANT', label: 'Merchant' },
  { value: 'AFFILIATE', label: 'Affiliate' },
];

const SYSTEM_TITLES = new Set([
  'Chainalysis Alert',
  'PEP Match',
  'SANCTION Match',
  'Screening Match',
  'Unacceptable user risk level',
]);

const RULES_ENGINE_NAMES = new Set([
  'affi_num_transactions_very_large_v3',
  'Affiliate_velocity_10_times_max_20_EUR_v2',
  'DOR_1TxOverThreshold_HR_v2',
  'DOR_1TxOverThreshold_MR_v2',
  'dor_high_risk_customer_v3',
  'DOR_TotalSpending_HR_v4',
  'DOR_TotalSpending_MR_v3',
  'DOR_TXWhilePEP/RCA_v2',
  'LargeTx_LIT_POL_24H_Incoming_v1',
  'LargeTx_LIT_POL_24H_Outgoing_v1',
  'LargeTx_LIT_POL_Incoming_v1',
  'LargeTx_LIT_POL_Outgoing_v1',
  'rule_account_holder_name_mismatch_v1',
  'rule_app_internal_transaction_v1',
  'rule_buy_frequent_new_customer_fraud_v1',
  'rule_elder_send_fraud_v1',
  'rule_high_risk_receive_buy_1_tx_over_threshold_v1',
  'rule_high_risk_receive_buy_31_days_over_threshold_v1',
  'rule_high_risk_receive_buy_7_days_over_threshold_v1',
  'rule_high_risk_send_1_transaction_over_threshold_v1',
  'rule_high_risk_send_31_days_over_threshold_v1',
  'rule_high_risk_send_7_days_over_threshold_v1',
  'rule_many_small_transactions_v1',
  'rule_medium_risk_receive_buy_1_tx_over_threshold_v1',
  'rule_medium_risk_receive_buy_31_days_over_threshold_v1',
  'rule_medium_risk_receive_buy_7_days_over_threshold_v1',
  'rule_medium_risk_send_1_transaction_over_threshold_v1',
  'rule_medium_risk_send_31_days_over_threshold_v1',
  'rule_medium_risk_send_7_days_over_threshold_v1',
  'rule_new_customer_large_buy_send_external_v1',
  'rule_send_new_customer_fraud_v1',
  'rule_too_many_cards_fraud_v1',
  'rule_transaction_country_blacklist_v1',
  'rule_underestimated_corporate_wallet_revenue_v1',
  'rule_underestimated_pay_revenue_v2',
  'rule_underestimated_turnover_v1',
]);

const isVisibleGroup = (key: string): boolean =>
  SYSTEM_TITLES.has(key) ||
  RULES_ENGINE_NAMES.has(key) ||
  key.startsWith('[CM1 Import] ');

const downloadCsv = (
  data: CaseStatisticsResponse,
  metric: Metric,
  periods: string[]
) => {
  const head = ['Rule', ...periods, 'Total'];
  const lookup = buildLookup(data);
  const lines = data.perGroup
    .filter((g) => isVisibleGroup(g.key))
    .map((group) => {
      const cells = periods.map((p) =>
        String(lookup.get(p)?.get(group.key)?.[metric] ?? 0)
      );
      return [group.key, ...cells, String(group[metric])].join(',');
    });
  const csv = [head.join(','), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `case-statistics-${metric}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

type Lookup = Map<string, Map<string, GroupCount>>;

const buildLookup = (data: CaseStatisticsResponse): Lookup => {
  const lookup: Lookup = new Map();
  data.periods.forEach((period) => {
    const byKey = new Map<string, GroupCount>();
    period.perGroup.forEach((group) => byKey.set(group.key, group));
    lookup.set(period.period, byKey);
  });
  return lookup;
};

export const Statistics = () => {
  const styles = useStyles();
  const { palette } = useTheme();
  const navigate = useNavigate();

  const [granularity, setGranularity] = useState<Granularity>('month');
  const [groupBy, setGroupBy] = useState<GroupBy>('rule');
  const [metric, setMetric] = useState<Metric>('created');
  const [customerType, setCustomerType] = useState('');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);

  const { data, isLoading, isError, refetch } = useGetCaseStatistics({
    granularity,
    groupBy,
    customerType: customerType || undefined,
    createdAfter: dateRange[0]?.toISOString(),
    createdBefore: dateRange[1]?.toISOString(),
  });

  const periods = useMemo(
    () => data?.periods.map((p) => p.period) ?? [],
    [data]
  );
  const lookup = useMemo<Lookup>(
    () =>
      data ? buildLookup(data) : new Map<string, Map<string, GroupCount>>(),
    [data]
  );

  const goToCases = (rule: string) => {
    const params = new URLSearchParams({ rule });
    if (metric === 'reportedToFipo') params.set('reportedToFipo', 'true');
    if (dateRange[0]) params.set('createdAfter', dateRange[0].toISOString());
    if (dateRange[1]) params.set('createdBefore', dateRange[1].toISOString());
    void navigate(`/fcu/cases?${params.toString()}`);
  };

  const headers = [
    { label: 'Rule', cellKey: 'rule' },
    ...periods.map((p) => ({ label: p, cellKey: p })),
    { label: 'Total', cellKey: 'total' },
  ];

  const rows =
    data?.perGroup
      .filter((group) => isVisibleGroup(group.key))
      .map((group) => ({
        id: group.key,
        value: {
          rule: (
            <UtilityButton
              variant="text"
              text={group.key}
              onClick={() => goToCases(group.key)}
            />
          ),
          ...Object.fromEntries(
            periods.map((p) => [
              p,
              lookup.get(p)?.get(group.key)?.[metric] ?? 0,
            ])
          ),
          total: group[metric],
        },
      })) ?? [];

  if (isError || (!isLoading && !data)) {
    return (
      <EmptyContent
        heading="Oops! Something went wrong."
        body="Error fetching statistics"
        action={
          <UtilityButton
            text="Try again"
            variant="primary"
            onClick={() => void refetch()}
          />
        }
      />
    );
  }

  return (
    <>
      <Header>
        <Typography variant="subtitle">Statistics</Typography>
      </Header>

      <div css={styles.filters}>
        <DateInput
          placeholder="All time"
          type="range"
          value={dateRange}
          onChange={(e: [Date | null, Date | null]) => setDateRange(e)}
          withTime
        />
        <UtilitySelect
          label="Granularity"
          listTitle="Granularity"
          options={GRANULARITY_OPTIONS}
          value={
            GRANULARITY_OPTIONS.find((o) => o.value === granularity) ?? null
          }
          onSelect={(d) => {
            if (d && !Array.isArray(d)) setGranularity(d.value as Granularity);
          }}
        />
        <UtilitySelect
          label="Group by"
          listTitle="Group by"
          options={GROUP_BY_OPTIONS}
          value={GROUP_BY_OPTIONS.find((o) => o.value === groupBy) ?? null}
          onSelect={(d) => {
            if (d && !Array.isArray(d)) setGroupBy(d.value as GroupBy);
          }}
        />
        <UtilitySelect
          label="Metric"
          listTitle="Metric"
          options={METRIC_OPTIONS}
          value={METRIC_OPTIONS.find((o) => o.value === metric) ?? null}
          onSelect={(d) => {
            if (d && !Array.isArray(d)) setMetric(d.value as Metric);
          }}
        />
        <UtilitySelect
          label="Customer type"
          listTitle="Customer type"
          options={CUSTOMER_TYPE_OPTIONS}
          value={
            CUSTOMER_TYPE_OPTIONS.find((o) => o.value === customerType) ?? null
          }
          onSelect={(d) => {
            if (d === null) setCustomerType('');
            else if (!Array.isArray(d)) setCustomerType(d.value);
          }}
        />
        <UtilityButton
          variant="secondary"
          text="Export CSV"
          disabled={!data || periods.length === 0}
          onClick={() => data && downloadCsv(data, metric, periods)}
        />
      </div>

      {data && (
        <div css={styles.kpis}>
          <KpiCard label="Created" value={data.totalCreated} />
          <KpiCard label="Managed (closed)" value={data.totalManaged} />
          <KpiCard
            label="Reported to FIPO"
            value={data.totalReportedToFipo}
            highlight={palette.warning.default}
          />
        </div>
      )}

      {isLoading && (
        <div css={styles.loadingContainer}>
          <Loader />
        </div>
      )}

      {!isLoading && data && data.perGroup.length === 0 && (
        <EmptyContent
          heading="No cases"
          body="No cases match the selected filters"
        />
      )}

      {!isLoading && data && data.perGroup.length > 0 && (
        <div css={styles.container}>
          <Typography variant="bodySmall">
            Showing <strong>{metric}</strong> counts. Click a rule to see the
            exact customers (CustomerID) in Cases.
          </Typography>
          <Table headers={headers} rows={rows} />
        </div>
      )}
    </>
  );
};

const KpiCard = ({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number;
  highlight?: string;
}) => {
  const { spacing } = useTheme();
  return (
    <Card css={{ padding: spacing.xs, minWidth: '160px' }}>
      <Typography variant="bodySmall">{label}</Typography>
      <Typography variant="heading2" color={highlight}>
        {value}
      </Typography>
    </Card>
  );
};

const useStyles = createStyles(({ spacing, palette }) => ({
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: spacing.xxs,
    marginTop: spacing.xxs,
    marginBottom: spacing.xs,
  },
  kpis: {
    display: 'flex',
    gap: spacing.xxs,
    marginBottom: spacing.s,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xxs,
    overflowX: 'auto',
    // Keep the first column (Rule) pinned while scrolling right.
    '& th:first-of-type, & td:first-of-type': {
      position: 'sticky',
      left: 0,
      zIndex: 1,
      backgroundColor: palette.white,
      borderRight: `1px solid ${palette.grey.x300}`,
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: spacing.l,
  },
}));
